import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ContactSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    inquiry: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const inquiryOptions = [
    { value: '', label: 'Select inquiry type' },
    { value: 'product-info', label: 'Product Information' },
    { value: 'pricing', label: 'Pricing & Quotation' },
    { value: 'technical-support', label: 'Technical Support' },
    { value: 'training', label: 'Training Programs' },
    { value: 'partnership', label: 'Partnership Opportunities' },
    { value: 'other', label: 'Other' }
  ];

  const contactInfo = [
    {
      icon: "MapPin",
      title: "Visit Our Office",
      details: [
        "Endosurgery Sdn Bhd",
        "Level 12, Menara Axiata",
        "No. 9, Jalan Stesen Sentral 5",
        "Kuala Lumpur Sentral",
        "50470 Kuala Lumpur, Malaysia"
      ],
      color: "primary"
    },
    {
      icon: "Phone",
      title: "Call Us",
      details: [
        "Main Line: +60 3-2274 8888",
        "Sales: +60 3-2274 8889",
        "Technical Support: +60 3-2274 8890",
        "Emergency: +60 12-345 6789"
      ],
      color: "success"
    },
    {
      icon: "Mail",
      title: "Email Us",
      details: [
        "info@endosurgery.com.my",
        "sales@endosurgery.com.my",
        "support@endosurgery.com.my",
        "training@endosurgery.com.my"
      ],
      color: "accent"
    },
    {
      icon: "Clock",
      title: "Business Hours",
      details: [
        "Monday - Friday: 8:00 AM - 6:00 PM",
        "Saturday: 9:00 AM - 1:00 PM",
        "Sunday: Closed",
        "Emergency Support: 24/7"
      ],
      color: "warning"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      inquiry: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Form submitted:', formData);
    setIsSubmitting(false);
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      inquiry: '',
      message: ''
    });
  };

  return (
    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Get In Touch
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Ready to enhance your medical practice with our premium equipment? 
            Contact our expert team for personalized consultation and support.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact Information</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                Our dedicated team is here to assist you with product inquiries, technical support, 
                and training programs. Reach out to us through any of the following channels.
              </p>
            </div>

            <div className="grid gap-6">
              {contactInfo?.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-slate-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600 hover:shadow-medical transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-${info?.color}/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon name={info?.icon} size={24} color={`var(--color-${info?.color})`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{info?.title}</h4>
                      <div className="space-y-1">
                        {info?.details?.map((detail, detailIndex) => (
                          <p key={detailIndex} className="text-gray-700 dark:text-gray-300 text-sm">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-muted rounded-xl overflow-hidden h-64"
            >
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="Endosurgery Malaysia Office Location"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=3.1319,101.6841&z=16&output=embed"
                className="border-0"
              />
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-medical border border-gray-200 dark:border-gray-600">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Send Us a Message</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    placeholder="Dr. John Smith"
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="john.smith@hospital.com"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Phone Number"
                    type="tel"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleInputChange}
                    placeholder="+60 12-345 6789"
                    required
                  />
                  <Input
                    label="Organization"
                    type="text"
                    name="organization"
                    value={formData?.organization}
                    onChange={handleInputChange}
                    placeholder="Hospital / Clinic Name"
                    required
                  />
                </div>

                <Select
                  label="Inquiry Type"
                  options={inquiryOptions}
                  value={formData?.inquiry}
                  onChange={handleSelectChange}
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData?.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Please provide details about your inquiry..."
                    required
                  />
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                  <Icon name="Shield" size={16} color="var(--color-success)" />
                  <span>Your information is secure and will not be shared with third parties.</span>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                >
                  {isSubmitting ? 'Sending Message...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Quick Contact Options */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              For urgent technical support or emergency equipment needs, contact our 24/7 support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="bg-white text-primary px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors duration-200 flex items-center justify-center space-x-2"
                onClick={() => window.open('tel:+60123456789', '_self')}
              >
                <Icon name="Phone" size={18} />
                <span>Call Emergency Line</span>
              </button>
              <button 
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary transition-all duration-200 flex items-center justify-center space-x-2"
                onClick={() => navigate('/contact')}
              >
                <Icon name="MessageCircle" size={18} />
                <span>Live Chat Support</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;